import { HttpError } from "./http.error";
import { LocalStorage } from "./local-storage";

export default class Http {
	private static defaultHeader: Record<string, string> = {};
	private static cachePromise = new Map<string, Promise<any>>();

	private static dummyMode = false;

	public static setDefaultHeader(header: Record<string, string>): void {
		Http.defaultHeader = {};

		Object.keys(header).forEach((key) => {
			if (header[key] !== undefined) {
				Http.defaultHeader[key] = header[key];
			}
		});
	}

	public static setDummyMode(dummyMode: boolean): void {
		Http.dummyMode = dummyMode;
	}

	public static async getWithCache<TReturn = any>(url: string, data?: any, init?: RequestInit): Promise<TReturn> {
		const fullUrl =
			url +
			((url.includes("?") ? "&" : "?") + new URLSearchParams(Http.removeEmpty(data)).toString()).replace(
				/^\?$/,
				"",
			);

		if (Http.cachePromise.has(fullUrl)) {
			return Http.cachePromise.get(fullUrl);
		}

		const localData = LocalStorage.get(fullUrl);

		if (localData) {
			const { ttl = 0, value } = JSON.parse(localData);

			if (Date.now() > ttl) {
				LocalStorage.remove(fullUrl);
			} else {
				return Promise.resolve(value as TReturn);
			}
		}

		const promise = Http.fetch(fullUrl, "GET", undefined, init).then((result) => {
			LocalStorage.set(fullUrl, JSON.stringify({ value: result, ttl: Date.now() + 3 * 60 * 60 * 1000 }));
			Http.cachePromise.delete(fullUrl);

			return result;
		});

		Http.cachePromise.set(fullUrl, promise);

		return promise;
	}

	public static async get<TReturn = any>(url: string, data?: any, init?: RequestInit): Promise<TReturn> {
		const params = (
			(url.includes("?") ? "&" : "?") + new URLSearchParams(Http.removeEmpty(data)).toString()
		).replace(/^\?$/, "");

		return Http.fetch(url + params, "GET", undefined, init);
	}

	public static async post<TReturn = any>(url: string, jsonData?: any, init?: RequestInit): Promise<TReturn> {
		return Http.fetch(url, "POST", jsonData, init);
	}

	public static async postFormData(url: string, formData: FormData): Promise<Response> {
		return Http.fetch(url, "POST", undefined, { body: formData });
	}

	public static async put<TReturn = any>(url: string, jsonData?: any, init?: RequestInit): Promise<TReturn> {
		return Http.fetch(url, "PUT", jsonData, init);
	}

	public static async patch<TReturn = any>(url: string, jsonData?: any, init?: RequestInit): Promise<TReturn> {
		return Http.fetch(url, "PATCH", jsonData, init);
	}

	public static async delete<TReturn = any>(url: string, jsonData?: any, init?: RequestInit): Promise<TReturn> {
		return Http.fetch(url, "DELETE", jsonData, init);
	}

	private static async fetch<Type = any>(
		url: string,
		method: string,
		jsonData: any,
		init: RequestInit = {},
	): Promise<Type> {
		const { headers = {}, body } = init || {};
		const settings = {
			...init,
			method,
		};

		if (Http.dummyMode) {
			return Promise.reject();
		}

		if (jsonData) {
			headers["Content-Type"] = "application/json";
			settings.body = JSON.stringify(jsonData);
		} else if (body) {
			settings.body = body;
		}

		settings.headers = new Headers({
			...Http.defaultHeader,
			...headers,
		});

		return fetch(url, settings).then((response) => {
			const contentType = response.headers.get("content-type");
			const contentLength = response.headers.get("content-length");

			if (contentLength !== "0" && contentType) {
				let promise;

				if (contentType.includes("application/json")) {
					promise = response.json().catch(() => {
						throw new HttpError(
							"Hiba az adatok feldolgozása közben!",
							response.status,
							undefined,
							jsonData,
							response,
						);
					});
				}

				if (contentType.includes("text/html") || contentType.includes("text/plain")) {
					promise = response.text();
				}

				if (promise) {
					return promise
						.then((data) => {
							if (response.ok) {
								return data;
							}

							throw new HttpError(
								data?.message || data?.error || response.statusText || "Unknown error",
								response.status,
								data,
								jsonData,
								response,
							);
						})
						.catch((reason) => {
							if ("status" in reason) {
								throw reason;
							}

							throw new HttpError(
								reason?.message || reason?.error || response.statusText || "Unknown error",
								response.status,
								undefined,
								jsonData,
								response,
							);
						});
				}
			}

			if (response.ok) {
				return response;
			}

			throw new HttpError(response.statusText, response.status, undefined, jsonData, response);
		});
	}

	public static dummyResponse<Type = Record<string, any>>(response: Type): Promise<Type> {
		return new Promise((resolve) => {
			setTimeout(() => {
				resolve(response);
			});
		});
	}

	private static removeEmpty(obj: Record<string, any>): Record<string, any> {
		const newObj = {};

		if (!obj) {
			return obj;
		}

		Object.keys(obj).forEach((key) => {
			if (obj[key] === Object(obj[key])) newObj[key] = Http.removeEmpty(obj[key]);
			else if (obj[key] !== undefined) newObj[key] = obj[key];
		});

		return newObj;
	}
}

export const http = Http;
